import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import { theme as appTheme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { RootState } from '../../redux/root-reducer';
import {
    fetchCampaignHostLtdPromotionAdmin,
    patchCampaignHostLtdPromotionAdmin,
} from '../../redux/configuration/action';
import type { ICampaignHostLtdPromotionAdminDraft } from '../../types/global/campaignHostAdmin.types';
import {
    adminResponseToDraft,
    buildCampaignHostStripePriceOptions,
    buildLtdPromotionAdminPatch,
    centsToDollarInput,
    dollarInputToCents,
} from '../../utils/campaignHostLtdAdminEditor';
import { showToastMessage } from '../../utils/AppUtils';
import { CustomDatePicker } from '../DatePicker/CustomDatePicker';
import { fetchCampaignHostPackages } from '../../redux/campaignHostSubscription/actions';
import {
    campaignHostCatalogPackagesSelector,
    campaignHostFetchingCatalogPackagesSelector,
} from '../../redux/campaignHostSubscription/selectors';

const accordionSx = (themeStyle: keyof typeof appTheme) => ({
    backgroundColor: 'transparent',
    color: appTheme[themeStyle].tag_white,
    '& .MuiAccordionSummary-root': { color: appTheme[themeStyle].tag_white },
    '& .MuiAccordionDetails-root': { color: appTheme[themeStyle].tag_white },
});

const fieldSx = (themeStyle: keyof typeof appTheme) => ({
    '& .MuiOutlinedInput-root': {
        color: appTheme[themeStyle].tag_white,
        '& fieldset': { borderColor: appTheme[themeStyle].form_divider },
        '&:hover fieldset': { borderColor: appTheme[themeStyle].highlight },
    },
    '& .MuiInputLabel-root': { color: appTheme[themeStyle].form_label },
    '& .MuiFormHelperText-root': {
        color: appTheme[themeStyle].form_label,
    },
});

const helperTextSx = (themeStyle: keyof typeof appTheme) => ({
    fontSize: 12,
    color: appTheme[themeStyle].form_label,
    mt: 0.75,
});

const selectSx = (themeStyle: keyof typeof appTheme) => ({
    ...fieldSx(themeStyle),
    '& .MuiSelect-select': { color: appTheme[themeStyle].tag_white },
    '& .MuiSelect-icon': { color: appTheme[themeStyle].form_label },
});

export interface GlobalConfigurationCampaignHostLtdEditorProps {
    open: boolean;
    themeStyle: keyof typeof appTheme;
}

export function GlobalConfigurationCampaignHostLtdEditor({
    open,
    themeStyle,
}: GlobalConfigurationCampaignHostLtdEditorProps) {
    const dispatch = useDispatch();
    const admin = useSelector((state: RootState) => state.configuration.campaignHostLtdAdmin);
    const loading = useSelector((state: RootState) => state.configuration.fetchingCampaignHostLtdAdmin);
    const saving = useSelector((state: RootState) => state.configuration.updatingCampaignHostLtdAdmin);

    const [snapshot, setSnapshot] = useState<ICampaignHostLtdPromotionAdminDraft | null>(null);
    const [draft, setDraft] = useState<ICampaignHostLtdPromotionAdminDraft | null>(null);
    const [promoDollars, setPromoDollars] = useState('');
    const [perCampaignDollars, setPerCampaignDollars] = useState('');
    const hostPackages = useSelector(campaignHostCatalogPackagesSelector);
    const loadingPackages = useSelector(campaignHostFetchingCatalogPackagesSelector);

    const promoRunning = Boolean(
        admin?.campaign_host_pricing?.ltd_promotion?.is_running ?? admin?.campaign_host_pricing?.ltd_promotion?.active,
    );

    const stripePriceOptions = useMemo(
        () => buildCampaignHostStripePriceOptions(hostPackages, draft?.stripe_price_id),
        [hostPackages, draft?.stripe_price_id],
    );

    const mergedDraft = useMemo((): ICampaignHostLtdPromotionAdminDraft | null => {
        if (!draft) return null;
        return {
            ...draft,
            promo_price_cents: promoDollars.trim() === '' ? null : dollarInputToCents(promoDollars),
            per_campaign_fee_cents: dollarInputToCents(perCampaignDollars) ?? draft.per_campaign_fee_cents,
        };
    }, [draft, promoDollars, perCampaignDollars]);

    const hasDraftChanges = useMemo(() => {
        if (!snapshot || !mergedDraft) return false;
        return Object.keys(buildLtdPromotionAdminPatch(snapshot, mergedDraft)).length > 0;
    }, [snapshot, mergedDraft]);

    const applyAdminToDraft = useCallback((data: NonNullable<typeof admin>) => {
        const next = adminResponseToDraft(data);
        setSnapshot(next);
        setDraft(next);
        setPromoDollars(centsToDollarInput(next.promo_price_cents));
        setPerCampaignDollars(centsToDollarInput(next.per_campaign_fee_cents));
    }, []);

    useEffect(() => {
        if (open) {
            dispatch(fetchCampaignHostLtdPromotionAdmin());
            dispatch(fetchCampaignHostPackages());
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (admin) {
            applyAdminToDraft(admin);
        }
    }, [admin, applyAdminToDraft]);

    const updateDraft = (patch: Partial<ICampaignHostLtdPromotionAdminDraft>) => {
        setDraft(prev => (prev ? { ...prev, ...patch } : prev));
    };

    const handleSave = () => {
        if (!snapshot || !mergedDraft) return;

        const patchBody = buildLtdPromotionAdminPatch(snapshot, mergedDraft);
        if (Object.keys(patchBody).length === 0) {
            showToastMessage('No changes to save', 'info');
            return;
        }

        dispatch(patchCampaignHostLtdPromotionAdmin(patchBody));
    };

    const menuPaperSx = {
        bgcolor: appTheme[themeStyle].deep_grey_lighter,
        color: appTheme[themeStyle].tag_white,
        '& .MuiMenuItem-root:hover': {
            bgcolor: appTheme[themeStyle].tag_white_light,
        },
    };

    return (
        <Accordion sx={accordionSx(themeStyle)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', pr: 1 }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16 }}>
                        Campaign Host LTD Promotion
                    </Typography>
                    {promoRunning && (
                        <Chip
                            label="Promo running"
                            size="small"
                            sx={{
                                bgcolor: 'rgba(0,108,80,0.35)',
                                color: '#7dffb8',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                            }}
                        />
                    )}
                    {admin && (
                        <Typography sx={{ fontSize: 12, color: appTheme[themeStyle].form_label }}>
                            ring0 · {admin.country}
                        </Typography>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Typography sx={{ fontSize: 13, color: appTheme[themeStyle].form_label, mb: 2 }}>
                    Canonical settings live on the ring0 / NG global configuration document. Checkout and brand pricing
                    use these values after save.
                </Typography>

                {loading && !draft ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} sx={{ color: appTheme[themeStyle].highlight }} />
                    </Box>
                ) : !draft ? (
                    <Alert severity="warning">Unable to load campaign host LTD settings.</Alert>
                ) : (
                    <Stack spacing={2.5}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={draft.enabled}
                                    onChange={e => updateDraft({ enabled: e.target.checked })}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: appTheme[themeStyle].highlight,
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: appTheme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff' }}>
                                        Enabled
                                    </Typography>
                                    <Typography sx={helperTextSx(themeStyle)}>
                                        Master switch — promo runs only when enabled and inside the date window (
                                        {promoRunning ? 'currently active' : 'not active'}).
                                    </Typography>
                                </Box>
                            }
                            sx={{ alignItems: 'flex-start', ml: 0 }}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                <CustomDatePicker
                                    label="Starts at"
                                    enableLabel
                                    value={draft.starts_at ?? undefined}
                                    onChange={iso => updateDraft({ starts_at: iso || null })}
                                    withTime
                                    withTimezone={false}
                                    uniformInputBlack
                                    calendarOnly
                                    sx={{ width: '100%' }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Button
                                        size="small"
                                        onClick={() => updateDraft({ starts_at: null })}
                                        sx={{
                                            color: appTheme[themeStyle].form_label,
                                            textTransform: 'none',
                                            fontSize: 12,
                                            minWidth: 0,
                                            px: 1,
                                        }}>
                                        Clear
                                    </Button>
                                </Box>
                                <Typography sx={helperTextSx(themeStyle)}>Leave empty for no start bound</Typography>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                <CustomDatePicker
                                    label="Ends at"
                                    enableLabel
                                    value={draft.ends_at ?? undefined}
                                    onChange={iso => updateDraft({ ends_at: iso || null })}
                                    withTime
                                    withTimezone={false}
                                    uniformInputBlack
                                    calendarOnly
                                    sx={{ width: '100%' }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Button
                                        size="small"
                                        onClick={() => updateDraft({ ends_at: null })}
                                        sx={{
                                            color: appTheme[themeStyle].form_label,
                                            textTransform: 'none',
                                            fontSize: 12,
                                            minWidth: 0,
                                            px: 1,
                                        }}>
                                        Clear
                                    </Button>
                                </Box>
                                <Typography sx={helperTextSx(themeStyle)}>Leave empty for no end bound</Typography>
                            </Box>
                        </Stack>

                        <FormControl fullWidth sx={selectSx(themeStyle)} disabled={loadingPackages}>
                            <InputLabel
                                id="campaign-host-stripe-price-label"
                                sx={{ color: appTheme[themeStyle].form_label }}>
                                Stripe promo price ID
                            </InputLabel>
                            <Select
                                labelId="campaign-host-stripe-price-label"
                                label="Stripe promo price ID"
                                value={draft.stripe_price_id}
                                onChange={e => updateDraft({ stripe_price_id: e.target.value })}
                                MenuProps={{ PaperProps: { sx: menuPaperSx } }}>
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {stripePriceOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText sx={{ color: appTheme[themeStyle].form_label }}>
                                {loadingPackages
                                    ? 'Loading campaign host packages…'
                                    : 'Required for discounted LTD checkout while promo is active'}
                            </FormHelperText>
                        </FormControl>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Promo display price (USD)"
                                fullWidth
                                value={promoDollars}
                                onChange={e => setPromoDollars(e.target.value)}
                                placeholder="99.99"
                                helperText="Display / audit only — Stripe charges the amount on stripe_price_id"
                                FormHelperTextProps={{ sx: { color: appTheme[themeStyle].form_label } }}
                                sx={fieldSx(themeStyle)}
                            />
                            <TextField
                                label="Per-campaign wallet fee (USD)"
                                fullWidth
                                value={perCampaignDollars}
                                onChange={e => setPerCampaignDollars(e.target.value)}
                                placeholder="50.00"
                                helperText="Charged when brand has no active host plan (default $50)"
                                FormHelperTextProps={{ sx: { color: appTheme[themeStyle].form_label } }}
                                sx={fieldSx(themeStyle)}
                            />
                        </Stack>

                        <Button
                            variant="contained"
                            disabled={saving || !hasDraftChanges}
                            onClick={handleSave}
                            sx={{
                                alignSelf: 'flex-start',
                                backgroundColor: appTheme[themeStyle].highlight,
                                color: appTheme[themeStyle].pure_black,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: appTheme[themeStyle].highlight,
                                    opacity: 0.9,
                                },
                                '&:disabled': {
                                    backgroundColor: appTheme[themeStyle].grey,
                                    color: appTheme[themeStyle].deep_grey,
                                },
                            }}>
                            {saving ? 'Saving…' : 'Save LTD promotion'}
                        </Button>
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>
    );
}
